import { ApiTranslation } from '../api-translation';
import { AuthedButton } from '../authed-button';
import { CurrencyView } from '../currency-view';
import { TextTooltip } from '../text-tooltip';
import { useBalance } from './hooks/use-balance';
import { extractTradeAmounts } from './receipt-events';
import { APP_CONTRACTS, IS_DEBUG, OPEN_SLIPPAGE } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { useSignedAllowance } from '@/client/hooks/use-signed-allowance';
import { useTradeGate } from '@/client/hooks/use-trade-gate';
import { useWallet } from '@/client/hooks/use-wallet';
import { useAppSettingStore } from '@/client/store/app-store';
import { hasPendingForMarket, usePendingTradesStore } from '@/client/store/pending-trades-store';
import { OptionSide, useTradeParamsStore, useTradeSettingStore } from '@/client/store/trade-store';
import { cn, getErrorMessage } from '@/client/utils';
import { api } from '@/client/utils/api';
import { publicClient } from '@/client/utils/chain-client';
import { handledKeyDownCallBackEnter } from '@/client/utils/handle';
import { isBoundWalletMismatch, truncateWallet } from '@/client/utils/wallet-address';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { useBuyWin } from '@/libs/react-sdk/contracts/use-buy-win';
import { useOptions } from '@/libs/react-sdk/contracts/use-options';
import { useSellWin } from '@/libs/react-sdk/contracts/use-sell-win';
import { predictionAbi } from '@/libs/sdk/contracts/abis/prediction';
import { useQuery } from '@tanstack/react-query';
import useDebounce from 'ahooks/lib/useDebounce';
import confetti from 'canvas-confetti';
import { useMemo, useRef, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Address, erc20Abi, maxUint256 } from 'viem';

// Smart auto-slippage: derive a min-receive buffer from the on-chain price impact.
// Returns buffer percentage in [0.5, 50].
const computeAutoBuffer = (
  type: 'buy' | 'sell',
  optionPrice: number, // 0..1 (probability)
  inputAmount: bigint,
  expectedOutput: bigint,
  decimal: number
): number => {
  const scale = 10 ** decimal;
  const amountNum = Number(inputAmount) / scale;
  const outputNum = Number(expectedOutput) / scale;

  let buffer = 0.5; // base: ~3s BSC block-to-block variance

  if (optionPrice > 0 && optionPrice < 1 && amountNum > 0 && outputNum > 0) {
    let priceImpact: number;
    if (type === 'buy') {
      const theoreticalOutput = amountNum / optionPrice; // spot rate = 1/price
      priceImpact = Math.max(0, (1 - outputNum / theoreticalOutput) * 100);
    } else {
      const theoreticalOutput = amountNum * optionPrice; // spot rate = price
      priceImpact = Math.max(0, (1 - outputNum / theoreticalOutput) * 100);
    }
    buffer = Math.max(0.5, priceImpact * 1.5 + 0.5);
  }

  // AMM curve is steep near 0/1 — widen the buffer at extreme probabilities.
  if (optionPrice < 0.05 || optionPrice > 0.95) {
    buffer = Math.max(buffer, 3);
  }

  return Math.min(50, Math.max(0.5, buffer));
};

type TradeConfirmProps = {
  type: 'buy' | 'sell';
  marketAddress: string;
  optionAddress?: string;
  optionSide?: OptionSide;
  amount?: bigint;
  minAmount?: bigint;
  deadline?: number;
  disabled?: boolean;
  uid: string;
  onSuccess?: () => void | Promise<void>;
};

export const TradeConfirm = ({
  type,
  marketAddress,
  optionAddress,
  optionSide,
  amount,
  deadline,
  disabled,
  uid,
  onSuccess,
}: TradeConfirmProps) => {
  const { t } = useTranslation();
  const [isTrading, setIsTrading] = useState(false);
  const [tradeStep, setTradeStep] = useState('');
  const [tradeTxHash, setTradeTxHash] = useState<string | undefined>();
  const tradingRef = useRef(false);
  const tradeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const bscScanUrl = tradeTxHash ? `https://bscscan.com/tx/${tradeTxHash}` : undefined;
  const { walletClient } = useWallet();
  const { linkedWallet } = useAuth();
  const tradeGate = useTradeGate();
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const market = useMarketDetail(api, { baseTokenAddress, marketAddress });
  const balance = useBalance({ type, marketAddress, optionAddress, uid });
  const baseToken = useMemo(
    () => ({
      address: market.data?.baseTokenAddress || baseTokenAddress,
      decimal: market.data?.decimal ?? 18,
    }),
    [market.data, baseTokenAddress]
  );
  const optionsList = useOptions(publicClient, marketAddress as Address);
  const currentOptionInfo = useMemo(
    () => market.data?.optionList.find(item => item.optionAddress.toLowerCase() === optionAddress?.toLowerCase()),
    [market.data?.optionList, optionAddress]
  );

  // Optimistic-trade gate (see pending-trades-store): while our own prior
  // trade on this market hasn't reconciled with the backend yet, block a
  // second submit rather than risk a duplicate/racing tx.
  const addPendingTrade = usePendingTradesStore(state => state.addPendingTrade);
  const pendingTrades = usePendingTradesStore(state => state.trades);
  const marketHasPending = useMemo(() => hasPendingForMarket(pendingTrades, marketAddress), [pendingTrades, marketAddress]);

  const isMultiOption = (market.data?.optionList.length ?? 0) > 2;

  // Option index: prefer on-chain options(), then PredictionCTF address suffix, then API order.
  const currentOptionsIndex = useMemo(() => {
    if (optionsList.data) {
      return optionsList.data.findIndex(item => item.toLowerCase() === optionAddress?.toLowerCase());
    }
    if (optionAddress) {
      const suffix = parseInt(optionAddress.slice(-1), 16);
      const maxIndex = market.data?.optionList?.length ?? 2;
      if (!isNaN(suffix) && suffix >= 0 && suffix < maxIndex) return suffix;
    }
    if (optionsList.isError && market.data?.optionList) {
      return market.data.optionList.findIndex(item => item.optionAddress.toLowerCase() === optionAddress?.toLowerCase());
    }
    return -1;
  }, [optionAddress, optionsList.data, optionsList.isError, market.data?.optionList]);

  const price = useMemo(() => currentOptionInfo?.price, [currentOptionInfo?.price]);
  // On-chain options() reverts by design on PredictionCTF pools, so an error
  // there is not itself a reason to block trading — the API optionList is the
  // fallback source. Only flag "unavailable" when we truly have no options to
  // trade against. (Before an outcome is picked the trade button stays disabled
  // via !optionAddress; the panel already prompts "please choose an outcome".)
  const tradingUnavailable = optionsList.isError && currentOptionsIndex === -1 && (market.data?.optionList?.length ?? 0) < 2;

  const CTF_ADDRESS = APP_CONTRACTS.CTF as Address | undefined;

  const buySimParams = useMemo(() => {
    if (!marketAddress || !amount || currentOptionsIndex == -1 || !baseToken) return undefined;
    let tokenAmount = amount;
    if (balance.data !== undefined) {
      tokenAmount = balance.data - amount < BigInt(Math.pow(10, balance.decimal - 2)) ? balance.data : amount;
    }
    return {
      address: marketAddress as Address,
      optionIndex: BigInt(currentOptionsIndex),
      tokenAmount,
    };
  }, [amount, balance.data, balance.decimal, baseToken, currentOptionsIndex, marketAddress]);

  const sellSimParams = useMemo(() => {
    if (!marketAddress || !amount || !balance.data || currentOptionsIndex === -1 || !currentOptionInfo) return undefined;
    const optionAmount = balance.data - amount < BigInt(Math.pow(10, balance.decimal - 2)) ? balance.data : amount;
    return {
      address: marketAddress as Address,
      optionsLength: optionsList.data?.length ?? market.data?.optionList?.length,
      optionIndex: BigInt(currentOptionsIndex),
      optionAmount,
    };
  }, [
    amount,
    balance,
    currentOptionInfo,
    currentOptionsIndex,
    marketAddress,
    optionsList.data?.length,
    market.data?.optionList?.length,
  ]);

  const debounceBuySimParams = useDebounce(buySimParams, { wait: 500 });
  const debounceSellSimParams = useDebounce(sellSimParams, { wait: 500 });

  const toBuyWin = useBuyWin(publicClient, debounceBuySimParams);
  const toSellWin = useSellWin(publicClient, debounceSellSimParams);
  const toWinRaw = type == 'buy' ? toBuyWin.data : toSellWin.data;
  // Pool fee (bps). getAmountsOut is the GROSS quote; the actual deposit/withdraw
  // applies this fee, so both the displayed amount and the min-receive must net it
  // out — otherwise min-receive sits above the real output and the tx reverts with
  // SlipTooMuch. Not exposed in market data, so read state() on-chain.
  const poolFee = useQuery({
    enabled: !!marketAddress,
    queryKey: ['poolFee', marketAddress],
    queryFn: async () =>
      // state() returns (fee, rewardPerShare) on the deployed pool — index [0]
      // is the fee in bps. Do NOT switch to [1] (that's rewardPerShare=0, which
      // would zero the fee-netting and bring back SlipTooMuch reverts).
      (
        (await publicClient.readContract({
          address: marketAddress as Address,
          abi: predictionAbi,
          functionName: 'state',
        })) as readonly [bigint, bigint]
      )[0],
  });
  const feeBps = poolFee.data ?? BigInt(0);
  const netToWinRaw = useMemo(
    () => (toWinRaw == null ? undefined : (toWinRaw * (BigInt(10000) - feeBps)) / BigInt(10000)),
    [toWinRaw, feeBps]
  );
  const toWin = useMemo(() => Number(netToWinRaw ?? BigInt(0)), [netToWinRaw]);

  const toWinLoading = useMemo(
    () => (type == 'buy' ? toBuyWin.isLoading : toSellWin.isLoading),
    [toBuyWin.isLoading, toSellWin.isLoading, type]
  );

  // Manual max-slippage (APMM-style) vs. Auto (price-impact buffer). Only consulted
  // when OPEN_SLIPPAGE exposes the control; otherwise Auto always.
  const manualSlippage = useTradeSettingStore(state => state.slippage);
  const slippageAuto = useTradeSettingStore(state => state.slippageAuto);
  const useManualSlippage = OPEN_SLIPPAGE && !slippageAuto;

  // Slippage-protected min-receive, derived from the live on-chain quote.
  // NOTE: do NOT use the `minAmount` prop — it is always 0 (legacy).
  const effectiveMinAmount = useMemo(() => {
    if (!toWinRaw || !netToWinRaw || !amount || !price) return BigInt(0);
    const decimal = baseToken.decimal;
    const optionPrice = Number(price) / Math.pow(10, currentOptionInfo?.decimal ?? decimal);
    // Price-impact buffer is measured on the GROSS quote (fee is handled separately
    // via netToWinRaw), then applied on top of the fee-netted output.
    // Manual mode: honor the user's max-slippage tolerance (floored at 0.5% so a
    // stray 0 can't send fully unprotected — the empty-quote case is still caught by
    // the minOptionAmount<=0 guard in handleBuy/handleSell). Auto: price-impact buffer.
    const buffer = useManualSlippage
      ? Math.min(50, Math.max(0.5, manualSlippage))
      : computeAutoBuffer(type, optionPrice, amount, toWinRaw, decimal);
    const bufferBps = BigInt(Math.ceil(buffer * 100));
    return (netToWinRaw * (BigInt(10000) - bufferBps)) / BigInt(10000);
  }, [
    toWinRaw,
    netToWinRaw,
    amount,
    price,
    type,
    baseToken.decimal,
    currentOptionInfo?.decimal,
    useManualSlippage,
    manualSlippage,
  ]);

  const buyParams = useMemo(() => {
    if (!buySimParams || deadline == undefined) return undefined;
    return { ...buySimParams, deadline: BigInt(deadline), minOptionAmount: effectiveMinAmount };
  }, [buySimParams, deadline, effectiveMinAmount]);

  const sellParams = useMemo(() => {
    if (!sellSimParams || deadline == undefined) return undefined;
    return { ...sellSimParams, deadline: BigInt(deadline), minTokenAmount: effectiveMinAmount };
  }, [sellSimParams, deadline, effectiveMinAmount]);

  const signedAllowance = useSignedAllowance(baseToken.address, marketAddress);
  const setAmount = useTradeParamsStore(state => state.setAmount);

  const isInsufficientFundsError = (error: any): boolean => {
    const msg = (error?.message || error?.shortMessage || '').toLowerCase();
    return (
      msg.includes('insufficient funds') || msg.includes('insufficient balance') || msg.includes('gas required exceeds')
    );
  };

  // Wrong-wallet backstop (ADR-051 D2), defense-in-depth behind tradeGate: this
  // reads walletClient.account — the account that actually signs — while tradeGate
  // reads the reactive wagmi address, which can lag it by one render on an account
  // switch. Never the only check: it returns false on a null linkedWallet.
  const assertBoundWallet = (): boolean => {
    if (isBoundWalletMismatch(linkedWallet, walletClient?.account?.address)) {
      toast.error(
        t('wrong-wallet', 'Wrong wallet connected. Switch to your linked wallet {{wallet}} to trade.', {
          wallet: truncateWallet(linkedWallet),
        })
      );
      return false;
    }
    return true;
  };

  const handleBuy = async () => {
    if (type != 'buy' || !marketAddress || !buyParams || price === undefined || !baseToken) return;
    // Fail-closed funnel (ADR-051 D2 hardening): the market page renders
    // <TradePanel> inline, so this signer is reachable without the option-button
    // gate. tradeGate blocks while the binding is unproven (DID /me still loading
    // → canTrade false), closing the window where assertBoundWallet passes on a
    // null linkedWallet.
    if (!tradeGate()) return false;
    if (!walletClient?.account) {
      toast.error(t('wallet-not-connected', 'Please connect your wallet'));
      return false;
    }
    if (!assertBoundWallet()) return false;
    if (Number(market.data?.deadline) <= Math.ceil(Date.now() / 1e3)) {
      toast.error(t('market-expired', 'Market has expired'));
      return false;
    }
    // Guard against unprotected (0-slippage) sends when the quote isn't ready.
    if (buyParams.minOptionAmount <= BigInt(0)) {
      toast.error(t('quote-not-ready', 'Price quote not ready, please retry'));
      return false;
    }
    const account = walletClient.account;
    const initialAccount = account.address;
    try {
      // Step 1: approve base token to the pool if needed
      const allowance = signedAllowance.data ?? BigInt(0);
      if (allowance < buyParams.tokenAmount) {
        setTradeStep(t('approving-token', 'Approving token...'));
        const approveArgs = {
          address: baseToken.address as Address,
          abi: erc20Abi,
          functionName: 'approve' as const,
          args: [marketAddress as Address, maxUint256] as const,
          account,
        };
        const approveGas = await publicClient.estimateContractGas(approveArgs);
        const approveTxHash = await walletClient.writeContract({
          ...approveArgs,
          chain: walletClient.chain,
          gas: (approveGas * 130n) / 100n,
        });
        setTradeTxHash(approveTxHash);
        await publicClient.waitForTransactionReceipt({ hash: approveTxHash });
      }

      if (walletClient.account?.address !== initialAccount) {
        toast.error(t('wallet-changed', 'Wallet account changed during trade. Please retry.'));
        return false;
      }

      // Step 2: deposit on the pool
      setTradeStep(t('confirming-trade', 'Confirming trade...'));
      const depositArgs = {
        address: marketAddress as Address,
        abi: predictionAbi,
        functionName: 'deposit' as const,
        args: [buyParams.optionIndex, buyParams.tokenAmount, buyParams.minOptionAmount, buyParams.deadline] as const,
        account,
      };
      const depositGas = await publicClient.estimateContractGas(depositArgs);
      const depositTxHash = await walletClient.writeContract({
        ...depositArgs,
        chain: walletClient.chain,
        gas: (depositGas * 130n) / 100n,
      });
      setTradeTxHash(depositTxHash);
      const receipt = await publicClient.waitForTransactionReceipt({ hash: depositTxHash });

      // waitForTransactionReceipt resolves (doesn't throw) for a mined-but-
      // reverted tx (e.g. a SlipTooMuch race between quote and inclusion) —
      // must not report success or create an optimistic row for a trade that
      // never happened.
      if (receipt.status !== 'success') {
        toast.error(t('trade-reverted', 'Transaction reverted on-chain. Please retry.'));
        return false;
      }

      const parsed = extractTradeAmounts(receipt, marketAddress, 'buy');
      addPendingTrade({
        txHash: depositTxHash,
        uid,
        marketAddress,
        optionAddress: optionAddress ?? '',
        side: 'buy',
        amountIn: (parsed?.amountIn ?? buyParams.tokenAmount).toString(),
        amountOut: (parsed?.amountOut ?? netToWinRaw)?.toString(),
        price,
        decimal: baseToken.decimal,
        optionDecimal: currentOptionInfo?.decimal,
        baseTokenAddress: baseToken.address,
        marketName: market.data?.marketName,
        optionName: currentOptionInfo?.description ?? currentOptionInfo?.optionName,
      });

      toast.success(t('buy-success'));
      setAmount(undefined);
      await onSuccess?.();
      return true;
    } catch (error) {
      if (IS_DEBUG) console.log('buy error', error);
      if (isInsufficientFundsError(error)) {
        toast.error(t('insufficient-gas', 'Insufficient BNB for gas fees. Please top up your wallet.'));
      } else {
        toast.error(getErrorMessage(error) ?? t('buy-failed'));
      }
      return false;
    }
  };

  const handleSell = async () => {
    if (type != 'sell' || !market.data || !marketAddress || !sellParams || price === undefined) return;
    // Fail-closed funnel (ADR-051 D2 hardening) — same ungated inline <TradePanel>
    // path as handleBuy, plus the sell modal (sell-context).
    if (!tradeGate()) return false;
    if (!walletClient?.account) {
      toast.error(t('wallet-not-connected', 'Please connect your wallet'));
      return false;
    }
    if (!assertBoundWallet()) return false;
    if (Number(market.data?.deadline) <= Math.ceil(Date.now() / 1e3)) {
      toast.error(t('market-expired', 'Market has expired'));
      return false;
    }
    if (sellParams.minTokenAmount <= BigInt(0)) {
      toast.error(t('quote-not-ready', 'Price quote not ready, please retry'));
      return false;
    }
    const account = walletClient.account;
    const initialAccount = account.address;
    try {
      // Step 0: ERC-1155 operator approval for the pool
      setTradeStep(t('checking-approval', 'Checking approval...'));
      if (CTF_ADDRESS) {
        const isApproved = await publicClient.readContract({
          address: CTF_ADDRESS,
          abi: [
            {
              type: 'function',
              name: 'isApprovedForAll',
              stateMutability: 'view',
              inputs: [
                { name: 'account', type: 'address' },
                { name: 'operator', type: 'address' },
              ],
              outputs: [{ name: '', type: 'bool' }],
            },
          ] as const,
          functionName: 'isApprovedForAll',
          args: [account.address, marketAddress as Address],
        });
        if (!isApproved) {
          setTradeStep(t('approving-token', 'Approving token...'));
          const approvalArgs = {
            address: CTF_ADDRESS,
            abi: [
              {
                type: 'function',
                name: 'setApprovalForAll',
                stateMutability: 'nonpayable',
                inputs: [
                  { name: 'operator', type: 'address' },
                  { name: 'approved', type: 'bool' },
                ],
                outputs: [],
              },
            ] as const,
            functionName: 'setApprovalForAll' as const,
            args: [marketAddress as Address, true] as const,
            account,
          };
          const approvalGas = await publicClient.estimateContractGas(approvalArgs);
          const approvalTxHash = await walletClient.writeContract({
            ...approvalArgs,
            chain: walletClient.chain,
            gas: (approvalGas * 130n) / 100n,
          });
          setTradeTxHash(approvalTxHash);
          await publicClient.waitForTransactionReceipt({ hash: approvalTxHash });
        }
      }

      // Guard a mid-flight MetaMask account switch between approval and withdraw
      // (parity with handleBuy) — otherwise the withdraw signs from a wallet that
      // may not hold the option tokens.
      if (walletClient.account?.address !== initialAccount) {
        toast.error(t('wallet-changed', 'Wallet account changed during trade. Please retry.'));
        return false;
      }

      // Step 1: withdraw on the pool
      setTradeStep(t('confirming-trade', 'Confirming trade...'));
      const withdrawArgs = {
        address: marketAddress as Address,
        abi: predictionAbi,
        functionName: 'withdraw' as const,
        args: [sellParams.optionIndex, sellParams.optionAmount, sellParams.minTokenAmount, sellParams.deadline] as const,
        account,
      };
      const withdrawGas = await publicClient.estimateContractGas(withdrawArgs);
      const withdrawTxHash = await walletClient.writeContract({
        ...withdrawArgs,
        chain: walletClient.chain,
        gas: (withdrawGas * 130n) / 100n,
      });
      setTradeTxHash(withdrawTxHash);
      const receipt = await publicClient.waitForTransactionReceipt({ hash: withdrawTxHash });

      // waitForTransactionReceipt resolves (doesn't throw) for a mined-but-
      // reverted tx (e.g. a SlipTooMuch race between quote and inclusion) —
      // must not report success or create an optimistic row for a trade that
      // never happened.
      if (receipt.status !== 'success') {
        toast.error(t('trade-reverted', 'Transaction reverted on-chain. Please retry.'));
        return false;
      }

      const parsed = extractTradeAmounts(receipt, marketAddress, 'sell');
      addPendingTrade({
        txHash: withdrawTxHash,
        uid,
        marketAddress,
        optionAddress: optionAddress ?? '',
        side: 'sell',
        amountIn: (parsed?.amountIn ?? sellParams.optionAmount).toString(),
        amountOut: (parsed?.amountOut ?? netToWinRaw)?.toString(),
        price,
        decimal: baseToken.decimal,
        optionDecimal: currentOptionInfo?.decimal,
        baseTokenAddress: baseToken.address,
        marketName: market.data?.marketName,
        optionName: currentOptionInfo?.description ?? currentOptionInfo?.optionName,
      });

      toast.success(t('sell-success'));
      setAmount(undefined);
      await onSuccess?.();
      return true;
    } catch (error) {
      if (IS_DEBUG) console.log('sell error', error);
      if (isInsufficientFundsError(error)) {
        toast.error(t('insufficient-gas', 'Insufficient BNB for gas fees. Please top up your wallet.'));
      } else {
        toast.error(getErrorMessage(error) ?? t('sell-failed'));
      }
      return false;
    }
  };

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (tradingRef.current) return;
    tradingRef.current = true;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    setIsTrading(true);
    setTradeStep('');
    setTradeTxHash(undefined);
    // 120s timeout protection
    tradeTimeoutRef.current = setTimeout(() => {
      if (tradingRef.current) {
        tradingRef.current = false;
        setIsTrading(false);
        setTradeStep('');
        setTradeTxHash(undefined);
        toast.error(t('trade-timeout', 'Trade timed out, please retry'));
      }
    }, 120_000);
    try {
      const success = await (type === 'buy' ? handleBuy() : handleSell());
      if (success) {
        confetti({ origin: { x: x / window.innerWidth, y: y / window.innerHeight } });
      }
      await (type == 'buy' ? toBuyWin.refetch({ cancelRefetch: false }) : toSellWin.refetch({ cancelRefetch: false }));
    } finally {
      clearTimeout(tradeTimeoutRef.current);
      tradingRef.current = false;
      setIsTrading(false);
      setTradeStep('');
      setTradeTxHash(undefined);
    }
  };

  return (
    <div className="w-full">
      {tradingUnavailable && (
        <div className="mx-2 mb-2 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs text-center">
          {t('trading-temporarily-unavailable', 'Trading temporarily unavailable for this market')}
        </div>
      )}
      {optionAddress && (amount != undefined || isTrading) && !tradingUnavailable && (
        <div className="flex mx-2 text-sm py-2 px-3 justify-center text-foreground/70 items-center gap-1 rounded-[16px_16px_0px_0px]">
          <span>
            {type === 'buy' && t('to-win')}
            {type === 'sell' && t('cash-out')}
          </span>
          <CurrencyView
            className="text-beneficial"
            animated={true}
            value={toWin}
            isLoading={toWinLoading}
            decimal={balance.decimal}
            baseTokenAddress={market.data?.baseTokenAddress}
            showIcon={true}
          />
        </div>
      )}
      {(() => {
        const confirmButton = (
          <AuthedButton
            className={cn(
              'w-full h-14 hover:text-white text-xl leading-6 font-medium select-none',
              'dark:text-background dark:bg-foreground dark:hover:bg-foreground/90 dark:hover:text-background/90',
              type == 'buy' && 'hover:bg-beneficial',
              type == 'sell' && 'hover:bg-destructive'
            )}
            isLoading={signedAllowance.isLoading}
            onKeyDown={handledKeyDownCallBackEnter(handleClick)}
            disabled={
              disabled ||
              tradingUnavailable ||
              toWinLoading ||
              isTrading ||
              !optionAddress ||
              !amount ||
              (isMultiOption && type === 'buy' && !optionSide) ||
              marketHasPending
            }
            onClick={handleClick}
            aria-live="polite"
          >
            {isTrading ? tradeStep || t('trading') : type === 'buy' ? t('buy') : t('sell')}
            {!isTrading && <ApiTranslation>{currentOptionInfo?.description}</ApiTranslation>}
            {!isTrading && isMultiOption && type === 'buy' && optionSide && (
              <span className="uppercase ms-1">{optionSide}</span>
            )}
          </AuthedButton>
        );
        return marketHasPending ? (
          // asChild: Radix Trigger must not render its own inline button, or the
          // full-width confirm button collapses to content width (nested <button>).
          <TextTooltip show asChild tooltip={t('prevTradeSyncing', 'Previous trade is still syncing')}>
            <span className="block w-full">{confirmButton}</span>
          </TextTooltip>
        ) : (
          confirmButton
        );
      })()}
      {bscScanUrl && isTrading && (
        <a
          href={bscScanUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-xs text-foreground/50 hover:text-foreground/70 mt-1 transition-colors"
        >
          🔗 {tradeTxHash?.slice(0, 6)}...{tradeTxHash?.slice(-4)}
        </a>
      )}
    </div>
  );
};
